<template>
  <div class="contract-applicant">
    <template v-for="(group, groupIndex) in fields">
      <h2
        v-if="value.hasCoApplicant && groupIndex === 1"
        :key="groupIndex + 'choice'"
        class="contract-applicant__coApplicant"
      >
        {{ $t('bidCoApplicant') }}
      </h2>

      <div class="contract-applicant__inputs" :key="groupIndex">
        <ChoiceComponent
          :value="groupIndex === 0 ? value.applicant.isCompany : value.coApplicant.isCompany"
          @input="val => (value[groupIndex === 0 ? 'applicant' : 'coApplicant'].isCompany = val)"
          class="contract-applicant__choice"
          :values="isCompanychoiceValues"
        >
        </ChoiceComponent>
        <SelectComponent
          class="contract-applicant__import"
          items="/api/resource/contact"
          search
          :empty-label="$t('importContact')"
          @input="$ev => fill(groupIndex === 0 ? 'applicant' : 'coApplicant', $ev)"
        />
        <InputComponent
          v-for="(field, index) in group"
          :key="index"
          :label="labels[field.key]"
          :value="$path(field.key, value)"
          @input="$ev => setValue(field.key, $ev)"
          :type="field.type || 'text'"
          :required="rulesComp[field.key].includes('required')"
          :disabled="disabledFields.includes(field.key)"
          :is-valid="validator.fields[field.key].valid"
          :validation-message="validator.fields[field.key].error"
          :mask="field.mask || {}"
          :autocomplete="field.autocomplete || false"
        />

        <CheckboxComponent v-if="groupIndex === 0" v-model="value.hasCoApplicant" :label="labels['hasCoApplicant']" />
      </div>
    </template>
  </div>
</template>
<script>
import { i18n } from '@/i18n'
import { mapi18n } from '@/i18n'
import Validator from '@/mixins/validator'
import { setByPath, PartialUpdate } from '@/utils'
import fetch from '@/fetch'

import Card from '@/components/Card'
import { InputComponent } from 'vue-elder-input'
import { CheckboxComponent } from 'vue-elder-checkbox'
import SelectComponent from '@kvass/custom-fields/src/Fields/SelectModal'

import { ChoiceComponent } from 'vue-elder-choice'

const Personi18nBuilder = prefix => {
  return {
    [`${prefix}.companyName`]: 'companyName',
    [`${prefix}.organizationNumber`]: 'orgNo',
    [`${prefix}.socialSecurityNumber`]: 'socialSecurityNumber',
    [`${prefix}.name`]: 'name',
    [`${prefix}.email`]: 'email',
    [`${prefix}.phone`]: 'phone',
    [`${prefix}.address.street`]: 'street',
    [`${prefix}.address.postcode`]: 'postcode',
    [`${prefix}.address.city`]: 'city',
  }
}

const Rules = {
  'applicant.companyName': `required_if:applicantCompanyFieldsRequired,${i18n.t('selected')}`,
  'applicant.organizationNumber': `required_if:applicantCompanyFieldsRequired,${i18n.t('selected')}`,
  'applicant.socialSecurityNumber': `required|socialSecurityNumber|different:coApplicant.socialSecurityNumber`,
  'applicant.name': 'required',
  'applicant.email': 'required|email|different:coApplicant.email',
  'applicant.phone': 'required|mobile',
  'applicant.address.street': 'required',
  'applicant.address.postcode': 'required',
  'applicant.address.city': 'required',

  'coApplicant.companyName': `required_if:coApplicantCompanyFieldsRequired,${i18n.t('selected')}`,
  'coApplicant.organizationNumber': `required_if:coApplicantCompanyFieldsRequired,${i18n.t('selected')}`,
  'coApplicant.socialSecurityNumber': `required_if:hasCoApplicant,${i18n.t(
    'yes',
  )}|socialSecurityNumber|different:applicant.socialSecurityNumber`,
  'coApplicant.name': `required_if:hasCoApplicant,${i18n.t('yes')}`,
  'coApplicant.email': `required_if:hasCoApplicant,${i18n.t('yes')}|email|different:applicant.email`,
  'coApplicant.phone': `required_if:hasCoApplicant,${i18n.t('yes')}|mobile`,
  'coApplicant.address.street': `required_if:hasCoApplicant,${i18n.t('yes')}`,
  'coApplicant.address.postcode': `required_if:hasCoApplicant,${i18n.t('yes')}`,
  'coApplicant.address.city': `required_if:hasCoApplicant,${i18n.t('yes')}`,
}

const Labels = {
  hasCoApplicant: 'bidHasCoApplicant',
  applicantCompanyFieldsRequired: 'company',
  coApplicantCompanyFieldsRequired: 'company',
  ...Personi18nBuilder('applicant'),
  ...Personi18nBuilder('coApplicant'),
}
function isValidCondition(field) {
  return !('condition' in field) || field.condition
}

export { Labels as labels, Rules as rules }
export default {
  mixins: [Validator],
  props: {
    value: Object,
    rules: Object,
    disabledFields: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      validator: {
        rules: 'rulesComp',
        labels: 'labels',
        data: 'data',
      },
    }
  },
  computed: {
    rulesComp() {
      return {
        ...Rules,
        ...this.rules,
      }
    },
    ...mapi18n('labels', Labels),
    coApplicantCompanyCondition() {
      return this.value.hasCoApplicant && this.value.coApplicant.isCompany
    },
    fields() {
      return [
        //applicant
        [
          {
            key: 'applicant.companyName',
            condition: this.value.applicant.isCompany,
          },
          {
            key: 'applicant.organizationNumber',
            condition: this.value.applicant.isCompany,
          },
          {
            key: 'applicant.socialSecurityNumber',
            mask: { mask: '000000 00000', unmask: true },
          },
          {
            key: 'applicant.name',
            autocomplete: 'name',
          },
          {
            key: 'applicant.email',
            autocomplete: 'email',
            type: 'email',
          },
          {
            key: 'applicant.phone',
            autocomplete: 'tel',
          },
          {
            key: 'applicant.address.street',
            autocomplete: 'street-address',
          },
          {
            key: 'applicant.address.postcode',
            autocomplete: 'postal-code',
            type: 'number',
          },
          {
            key: 'applicant.address.city',
            autocomplete: 'address-level2',
          },
        ].filter(isValidCondition),

        //coApplicant
        [
          {
            key: 'coApplicant.companyName',
            condition: this.coApplicantCompanyCondition,
          },
          {
            key: 'coApplicant.organizationNumber',
            condition: this.coApplicantCompanyCondition,
          },
          {
            key: 'coApplicant.socialSecurityNumber',
            mask: { mask: '000000 00000', unmask: true },
            condition: this.value.hasCoApplicant,
          },
          {
            key: 'coApplicant.name',
            autocomplete: 'name',
            condition: this.value.hasCoApplicant,
          },
          {
            key: 'coApplicant.email',
            autocomplete: 'email',
            type: 'email',
            condition: this.value.hasCoApplicant,
          },
          {
            key: 'coApplicant.phone',
            autocomplete: 'tel',
            type: 'number',
            condition: this.value.hasCoApplicant,
          },
          {
            key: 'coApplicant.address.street',
            autocomplete: 'street-address',
            condition: this.value.hasCoApplicant,
          },
          {
            key: 'coApplicant.address.postcode',
            autocomplete: 'postal-code',
            type: 'number',
            condition: this.value.hasCoApplicant,
          },
          {
            key: 'coApplicant.address.city',
            autocomplete: 'address-level2',
            condition: this.value.hasCoApplicant,
          },
        ].filter(isValidCondition),
      ].filter(e => e.length)
    },
    isCompanychoiceValues() {
      return [
        { value: false, label: this.$t('private') },
        { value: true, label: this.$t('company') },
      ]
    },
    data() {
      return {
        ...this.value,
        hasCoApplicant: this.value.hasCoApplicant ? this.$t('yes') : this.$t('no'),
        applicantCompanyFieldsRequired: this.value.applicant.isCompany ? this.$t('selected') : this.$t('no'),
        coApplicantCompanyFieldsRequired: this.coApplicantCompanyCondition ? this.$t('selected') : this.$t('no'),
      }
    },
  },
  methods: {
    setValue(path, value) {
      return setByPath(path, value, this.value)
    },
    fill(type, id) {
      fetch(
        `/api/resource/contact?ids=${id}&field.name=name&field.phone=phone&field.email=email&field.address=address&field.company=company`,
      )
        .then(res => res.json())
        .then(data => data[0])
        .then(data => {
          const { address: addressRaw, company, ...rest } = data
          const { id, county, ...address } = addressRaw || {}
          const { name: companyName, organizationNumber } = company || {}

          let result = { ...rest, address, companyName, organizationNumber }
          if (data.company) result.isCompany = true

          PartialUpdate(this.value[type], result)
        })
    },
  },
  components: {
    Card,
    InputComponent,
    CheckboxComponent,
    ChoiceComponent,
    SelectComponent,
  },
}
</script>

<style lang="scss">
.contract-applicant {
  &__choice {
    margin-bottom: -0.5rem;

    .elder-choices__item-label {
      padding: 0.5rem 3rem;
    }
    .elder-choices__item {
      border-radius: 50px;
      flex-grow: 0;
    }
  }

  &__import {
    .elder-input__field {
      border: none;
    }

    .field-select-modal__empty-icon {
      display: none;
    }

    .kvass-selector {
      padding: 0;

      .elder-input__field {
        border: 1px solid var(--vue-elder-border-color, #eaeaea);
      }

      .kvass-selector__input {
        text-align: right;
        .fade {
          opacity: 1;
          color: var(--primary);
          text-decoration: underline;
        }
        &-element {
          width: 0;
        }
      }
    }
  }

  &__inputs {
    display: grid;
    grid-gap: 1rem 2rem;
    align-items: flex-start;
    grid-template-columns: 1fr;

    @media (min-width: 800px) {
      grid-template-columns: repeat(2, 1fr);

      .elder-checkbox,
      h2 {
        grid-column-end: span 2;
      }
    }

    h2 {
      margin: 2rem 0 0;
    }
  }
}
</style>
